<template>
  <div class="network-profile-form">
    <h3 class="text-subtitle-1 font-weight-bold mb-1">Red Pawer</h3>
    <p class="text-body-2 grey--text text--darken-1 mb-4">
      Datos que se muestran en la app a los tutores con coberturas disponibles.
    </p>

    <v-alert v-if="missing.length" type="warning" dense outlined class="mb-4">
      No se puede mostrar en la app. Falta: {{ missingText }}.
    </v-alert>

    <v-form ref="form">
      <v-row class="my-1">
        <v-col cols="12" sm="6">
          <v-select
            label="Tipo"
            :items="typeOptions"
            :value="value.type"
            :disabled="disabled"
            placeholder="Sin definir"
            outlined
            dense
            hide-details="auto"
            @change="update('type', $event)"
          ></v-select>
        </v-col>
        <v-col cols="12" sm="6">
          <v-text-field
            label="WhatsApp"
            :value="value.whatsapp"
            :rules="whatsappRules"
            :disabled="disabled"
            placeholder="+56 9 1234 5678"
            outlined
            dense
            hide-details="auto"
            @input="update('whatsapp', $event)"
          ></v-text-field>
        </v-col>
      </v-row>

      <v-row v-if="value.type === 'clinic'" class="my-1">
        <v-col cols="12" sm="7">
          <v-text-field
            label="Dirección"
            :value="value.address"
            :disabled="disabled"
            placeholder="Calle, número y local"
            outlined
            dense
            hide-details="auto"
            @input="update('address', $event)"
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="5">
          <v-autocomplete
            label="Comuna"
            :items="communes"
            :value="value.commune"
            item-text="name"
            item-value="code"
            return-object
            clearable
            :loading="loadingCommunes"
            :disabled="disabled || loadingCommunes"
            no-data-text="No se encontraron comunas"
            outlined
            dense
            hide-details="auto"
            @change="update('commune', $event || null)"
          ></v-autocomplete>
        </v-col>
      </v-row>

      <v-row v-if="value.type === 'home_visit'" class="my-1">
        <v-col cols="12">
          <v-autocomplete
            label="Comunas donde atiende"
            :items="communes"
            :value="value.homeVisitCommunes"
            item-text="name"
            item-value="code"
            return-object
            multiple
            chips
            small-chips
            deletable-chips
            clearable
            :loading="loadingCommunes"
            :disabled="disabled || loadingCommunes"
            no-data-text="No se encontraron comunas"
            outlined
            dense
            hide-details="auto"
            @change="update('homeVisitCommunes', $event || [])"
          ></v-autocomplete>
        </v-col>
      </v-row>

      <div class="opening-hours mt-4">
        <h4 class="text-subtitle-2 font-weight-bold mb-1">Horario de atención (opcional)</h4>
        <p class="text-body-2 grey--text text--darken-1 mb-2">
          Si no lo completas, la app no muestra el horario.
        </p>
        <div v-for="(day, dayIndex) in days" :key="day.key" class="opening-day d-flex align-center flex-wrap">
          <span class="opening-day__label text-body-2">{{ day.label }}</span>
          <v-checkbox
            :input-value="day.closed"
            :disabled="disabled"
            label="Cerrado"
            dense
            hide-details
            class="mt-0 mr-4"
            @change="setClosed(dayIndex, $event)"
          ></v-checkbox>
          <template v-if="!day.closed">
            <div v-for="(range, rangeIndex) in day.ranges" :key="rangeIndex" class="d-flex align-center mr-4">
              <v-select
                :items="timeOptions"
                :value="range.from"
                :disabled="disabled"
                outlined
                dense
                hide-details
                class="opening-time"
                @change="setTime(dayIndex, rangeIndex, 'from', $event)"
              ></v-select>
              <span class="mx-2 text-body-2">a</span>
              <v-select
                :items="timeOptions"
                :value="range.to"
                :disabled="disabled"
                outlined
                dense
                hide-details
                class="opening-time"
                @change="setTime(dayIndex, rangeIndex, 'to', $event)"
              ></v-select>
              <v-btn
                v-if="rangeIndex > 0"
                icon
                small
                :disabled="disabled"
                aria-label="Quitar tramo"
                @click="removeRange(dayIndex, rangeIndex)"
              >
                <v-icon small>mdi-close</v-icon>
              </v-btn>
            </div>
            <v-btn
              v-if="day.ranges.length < maxRanges"
              text
              small
              color="primary"
              :disabled="disabled"
              @click="addRange(dayIndex)"
            >
              Agregar tramo
            </v-btn>
          </template>
        </div>
        <p v-if="hoursError" class="error--text text-body-2 mt-2 mb-0">{{ hoursError }}</p>
      </div>
    </v-form>

    <p v-if="communesError" class="error--text text-body-2 mt-2 mb-0">
      No se pudo cargar el listado de comunas. Cierra y vuelve a abrir el formulario.
    </p>
  </div>
</template>

<script>
import AddressService from '../services/address.service';
import {
  MAX_OPENING_RANGES,
  NETWORK_TYPE_OPTIONS,
  OPENING_TIME_OPTIONS,
  communeKey,
  defaultOpeningRange,
  formatMissingFields,
  isValidChileWhatsapp,
  openingHoursError,
  toOpeningHoursForm
} from '../utils/red-pawer.utils';

export default {
  name: 'DroopyNetworkProfileForm',

  props: {
    // { type, whatsapp, address, commune: {code,name}|null, homeVisitCommunes: {code,name}[],
    //   openingHours: [{ key, label, closed, ranges: [{from,to}] }] }
    value: { type: Object, required: true },
    // Campos que el servidor dijo que faltan (`missing` del PUT o del 422).
    missing: { type: Array, default: () => [] },
    disabled: { type: Boolean, default: false }
  },

  data() {
    return {
      typeOptions: NETWORK_TYPE_OPTIONS,
      timeOptions: OPENING_TIME_OPTIONS,
      maxRanges: MAX_OPENING_RANGES,
      communes: [],
      loadingCommunes: false,
      communesError: false,
      whatsappRules: [
        v => isValidChileWhatsapp(v) || 'Ingresa un celular chileno, por ejemplo +56 9 1234 5678'
      ]
    };
  },

  computed: {
    missingText() {
      return formatMissingFields(this.missing);
    },

    days() {
      return this.value.openingHours || toOpeningHoursForm(null);
    },

    hoursError() {
      return openingHoursError(this.days);
    }
  },

  watch: {
    'value.commune'() {
      this.resolveCommuneCode();
    }
  },

  created() {
    this.fetchCommunes();
  },

  methods: {
    /** Validates the fields this form owns: the WhatsApp format and the opening hours. */
    validate() {
      const fieldsValid = this.$refs.form ? this.$refs.form.validate() : true;
      return fieldsValid && !this.hoursError;
    },

    updateDay(dayIndex, changes) {
      const days = this.days.map((day, i) => (i === dayIndex ? { ...day, ...changes } : day));
      this.update('openingHours', days);
    },

    setClosed(dayIndex, closed) {
      const day = this.days[dayIndex];
      const ranges = closed || day.ranges.length ? day.ranges : [defaultOpeningRange(null)];
      this.updateDay(dayIndex, { closed: !!closed, ranges });
    },

    setTime(dayIndex, rangeIndex, field, time) {
      const ranges = this.days[dayIndex].ranges.map((range, i) =>
        i === rangeIndex ? { ...range, [field]: time } : range
      );
      this.updateDay(dayIndex, { ranges });
    },

    addRange(dayIndex) {
      const ranges = this.days[dayIndex].ranges;
      this.updateDay(dayIndex, { ranges: [...ranges, defaultOpeningRange(ranges[ranges.length - 1])] });
    },

    removeRange(dayIndex, rangeIndex) {
      this.updateDay(dayIndex, { ranges: this.days[dayIndex].ranges.filter((_, i) => i !== rangeIndex) });
    },

    update(field, fieldValue) {
      // `input` con el objeto completo: el padre es dueño del modelo (v-model).
      this.$emit('input', { ...this.value, [field]: fieldValue });
    },

    async fetchCommunes() {
      this.loadingCommunes = true;
      const res = await AddressService.getAllCommunes();
      if (res.status === 'ok') {
        this.communes = res.data
          .map(c => ({ code: String(c.codigo), name: c.nombre }))
          .sort((a, b) => a.name.localeCompare(b.name, 'es'));
        this.communesError = false;
        this.resolveCommuneCode();
      } else {
        this.communesError = true;
      }
      this.loadingCommunes = false;
    },

    // Sin perfil guardado, el backend precarga la comuna desde `clinics.county`, que
    // puede venir solo con nombre: se busca su código en el catálogo para mostrarla.
    resolveCommuneCode() {
      const commune = this.value.commune;
      if (!commune || commune.code || !commune.name || !this.communes.length) return;
      const match = this.communes.find(c => communeKey(c.name) === communeKey(commune.name));
      if (match) this.update('commune', match);
    }
  }
};
</script>

<style scoped>
.network-profile-form {
  width: 100%;
}
.opening-day {
  min-height: 48px;
  gap: 4px 0;
}
.opening-day__label {
  width: 96px;
}
.opening-time {
  max-width: 110px;
}
</style>
